import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, X, Coins, Sparkles, Trophy, Users, Flame, Crown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { useAuth } from "../../contexts/AuthContext";

export default function FreeSpinPage() {
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [skipAnim, setSkipAnim] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [tierIdx, setTierIdx] = useState(0);
  const { user, refreshUser } = useAuth();
  const canvasRef = useRef(null);

  // fake recent winners ticker (atmospheric)
  const TIERS = useMemo(
    () => [
      { id: "free", label: "Free", cost: 0, free: true },
    ],
    []
  );

  const load = async () => {
    try {
      const { data } = await api.get("/profile/free-spin/status");
      setStatus(data);
      setCountdown(data.seconds_remaining || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load spin status");
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { load(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [countdown]);

  useEffect(() => {
    if (!status?.prizes?.length || !canvasRef.current) return;
    drawBajiWheel(canvasRef.current, status.prizes);
  }, [status]);

  const fmtTime = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const spin = async () => {
    const tier = TIERS[tierIdx];
    if (!tier.free) {
      toast.info("This tier is coming soon — please use Free tier for now.");
      return;
    }
    if (!status?.can_spin || spinning) return;
    setSpinning(true);
    setResult(null);
    try {
      const { data } = await api.post("/profile/free-spin");
      const n = status.prizes.length;
      const seg = 360 / n;
      const midOfIdx = data.prize_index * seg + seg / 2;
      const duration = skipAnim ? 300 : 5200;
      const turns = skipAnim ? 1 : 8;
      const target = 360 * turns - midOfIdx;
      setRotation(target);
      setTimeout(async () => {
        setSpinning(false);
        setResult({ label: data.label, amount: data.amount, color: data.color });
        if (data.amount > 0) {
          setCelebrate(true);
          setTimeout(() => setCelebrate(false), 3600);
          toast.success(`🎉 You won ${data.label}! BDT ${data.amount.toFixed(2)} credited.`);
        } else {
          toast.info("Better luck next time!");
        }
        await refreshUser?.();
        await load();
      }, duration);
    } catch (e) {
      setSpinning(false);
      toast.error(e?.response?.data?.detail || "Spin failed");
    }
  };

  const jackpot = useMemo(() => {
    if (!status?.prizes?.length) return 0;
    return Math.max(...status.prizes.map((p) => Number(p.amount) || 0));
  }, [status]);

  const points = user?.vip_points || 0;

  // simulated winner ticker
  const tickerItems = useMemo(() => {
    const names = ["mdjah", "rahim", "sakib", "farhan", "nayeem", "rakib", "rashed", "tanvir", "karim", "hasan"];
    const amts = [40, 25, 500, 100, 10, 250, 5, 65, 1000, 50];
    return names.map((n, i) => {
      const hash = Math.floor(1000 + (i * 7919) % 9000);
      return `Congrats ${n}****r${hash}! Won ৳${amts[i]} free spin!`;
    });
  }, []);

  if (!status) {
    return (
      <ProfileShell title="Daily Free Spin">
        <div className="py-10 text-center text-white/60">Loading...</div>
      </ProfileShell>
    );
  }

  if (!status.enabled) {
    return (
      <ProfileShell title="Daily Free Spin">
        <div className="py-20 text-center" data-testid="free-spin-disabled">
          <Coins size={40} className="mx-auto text-white/30 mb-3" />
          <div className="text-white/70">Free spin is currently unavailable.</div>
        </div>
      </ProfileShell>
    );
  }

  return (
    <ProfileShell title="Daily Free Spin">
      <div
        className="relative pb-28 sm:pb-10 overflow-hidden rounded-2xl"
        data-testid="free-spin-page"
        style={{
          background:
            "radial-gradient(900px 600px at 50% 30%, rgba(16,103,68,0.55) 0%, rgba(8,42,29,0.85) 55%, #051a12 100%)",
          boxShadow: "inset 0 0 0 1px rgba(234,179,8,0.12)",
        }}
      >
        {/* Top bar: Rules link + Close icon (visual) */}
        <div className="flex items-center justify-between px-3 pt-3 sm:px-4 sm:pt-4">
          <button
            onClick={() => setShowRules(true)}
            className="px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[12px] sm:text-sm font-semibold text-amber-200/90 border border-amber-300/40 bg-black/30 hover:bg-amber-400/10 transition inline-flex items-center gap-1.5"
            data-testid="free-spin-rules-btn"
          >
            <Crown size={13} className="text-amber-300" />
            Rules
          </button>
          <div className="text-[10px] sm:text-[11px] text-amber-300/80 uppercase tracking-[0.2em] sm:tracking-[0.28em] font-bold inline-flex items-center gap-1.5">
            <Sparkles size={10} className="animate-pulse" />
            Daily Spin
            <Sparkles size={10} className="animate-pulse" />
          </div>
        </div>

        {/* ===== Premium Hero (compact on mobile) ===== */}
        <div className="px-3 pt-3 sm:px-4 sm:pt-5 text-center relative">
          {/* Eyebrow tag */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-bold tracking-[0.18em] sm:tracking-[0.22em] uppercase mb-2"
            style={{
              background: "linear-gradient(180deg, rgba(220,38,38,0.85), rgba(127,29,29,0.85))",
              color: "#fef3c7",
              border: "1px solid rgba(254,243,199,0.4)",
              boxShadow: "0 4px 14px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
            }}
          >
            <Flame size={10} className="text-amber-200" />
            Today&apos;s Jackpot
            <Flame size={10} className="text-amber-200" />
          </div>

          {/* Main headline w/ gold gradient + animated shine */}
          <h2
            className="text-[20px] sm:text-[40px] font-black tracking-tight leading-none relative inline-block overflow-hidden"
            style={{
              fontFamily: "Impact, 'Arial Black', system-ui, sans-serif",
              letterSpacing: "0.015em",
              backgroundImage:
                "linear-gradient(180deg, #fff7d6 0%, #fde047 35%, #f59e0b 60%, #b45309 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter:
                "drop-shadow(0 2px 0 #78350f) drop-shadow(0 0 22px rgba(253,224,71,0.45))",
            }}
          >
            GET UP TO ৳{jackpot.toFixed(0)} FOR FREE!
          </h2>

          <div className="mt-1.5 sm:mt-2 text-[11px] sm:text-[13px] text-emerald-200/85 font-medium px-2">
            Spin · Win instantly · Withdraw anytime
          </div>
        </div>

        {/* ===== Social-proof Stats Strip (compact labels) ===== */}
        <div className="mt-3 sm:mt-4 px-3 grid grid-cols-3 gap-1.5 sm:gap-2">
          {[
            { icon: Trophy, label: "Won",      value: `৳${(184520).toLocaleString()}`, color: "#fbbf24", glow: "rgba(251,191,36,0.35)" },
            { icon: Users,  label: "Players",  value: "14,832",                     color: "#34d399", glow: "rgba(52,211,153,0.35)" },
            { icon: Flame,  label: "Top win",  value: "৳5,000",                     color: "#f87171", glow: "rgba(248,113,113,0.35)" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="rounded-lg sm:rounded-xl px-1.5 py-1.5 sm:px-2.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 min-w-0"
                style={{
                  background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.25))",
                  border: "1px solid rgba(253,224,71,0.18)",
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 18px ${s.glow}`,
                }}
              >
                <span
                  className="h-6 w-6 sm:h-8 sm:w-8 rounded-md sm:rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: `radial-gradient(circle at 35% 28%, rgba(255,255,255,0.18), rgba(0,0,0,0.4))`,
                    border: `1px solid ${s.color}55`,
                  }}
                >
                  <Icon size={12} className="sm:hidden" style={{ color: s.color }} />
                  <Icon size={15} className="hidden sm:block" style={{ color: s.color }} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-white/55 font-semibold leading-none truncate">
                    {s.label}
                  </div>
                  <div className="text-[10.5px] sm:text-sm font-black text-white truncate tabular-nums leading-tight mt-0.5">
                    {s.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ===== Winners ticker ===== */}
        <div className="mt-2.5 sm:mt-3 mx-3 rounded-full overflow-hidden border border-emerald-400/25 bg-black/40 py-1.5 relative">
          {/* Edge fade masks for cinematic feel */}
          <span className="absolute inset-y-0 left-0 w-10 z-10 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.85), transparent)" }} />
          <span className="absolute inset-y-0 right-0 w-10 z-10 pointer-events-none" style={{ background: "linear-gradient(-90deg, rgba(0,0,0,0.85), transparent)" }} />
          <div className="whitespace-nowrap flex gap-10 animate-[marquee_28s_linear_infinite]">
            {[...tickerItems, ...tickerItems].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-[12px] text-amber-100/90 px-2">
                <GoldCoin size={14} />
                <span className="text-white/90">{t}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Celebration */}
        {celebrate && (
          <>
            <Confetti />
            <CoinRain />
          </>
        )}

        {/* ===== Wheel stage ===== */}
        <div className="flex flex-col items-center relative mt-3 sm:mt-6 px-3">
          <div
            className="relative w-[min(72vw,300px)] sm:w-[min(94vw,380px)] aspect-square"
          >
            {/* Massive yellow halo behind wheel (Baji-style radiance) */}
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                inset: -40,
                background:
                  "radial-gradient(circle, rgba(253,224,71,0.55) 0%, rgba(253,224,71,0.25) 35%, rgba(253,224,71,0) 62%)",
                filter: "blur(18px)",
              }}
            />
            {/* Secondary tighter glow */}
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                inset: -4,
                background:
                  "radial-gradient(circle, rgba(253,224,71,0.45) 0%, rgba(253,224,71,0) 55%)",
                filter: "blur(6px)",
              }}
            />
            {/* Rotating conic light sweep */}
            <div
              className="absolute inset-0 rounded-full wheel-rays pointer-events-none"
              style={{
                background:
                  "conic-gradient(from 0deg, rgba(255,245,180,0.8) 0deg, rgba(253,224,71,0) 35deg, rgba(253,224,71,0) 175deg, rgba(255,245,180,0.7) 205deg, rgba(253,224,71,0) 245deg, rgba(253,224,71,0) 360deg)",
                filter: "blur(8px)",
                opacity: 0.9,
              }}
            />

            {/* Decorative twinkling LIGHT BULBS around outer rim — Vegas casino vibe */}
            <div className="absolute inset-0 pointer-events-none z-[5]">
              {Array.from({ length: 24 }).map((_, i) => {
                const angle = (i * 360) / 24;
                const rad = (angle * Math.PI) / 180;
                const r = 50; // radius % from center
                const x = 50 + r * Math.cos(rad);
                const y = 50 + r * Math.sin(rad);
                return (
                  <span
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      width: 6,
                      height: 6,
                      transform: "translate(-50%, -50%)",
                      background:
                        "radial-gradient(circle at 35% 30%, #fffbeb, #fde047 50%, #b45309 100%)",
                      boxShadow:
                        "0 0 6px rgba(253,224,71,0.95), 0 0 12px rgba(253,224,71,0.6)",
                      animation: `bulbTwinkle 1.4s ease-in-out ${i * 0.07}s infinite`,
                    }}
                  />
                );
              })}
            </div>

            {/* ==== ORNATE GOLD BEZEL (multi-layer) ==== */}
            {/* Layer 1 — thick metallic gold ring with highlight */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "conic-gradient(from -90deg, #fff4c4 0deg, #f5b843 18deg, #9a5a0d 36deg, #fde68a 70deg, #f59e0b 108deg, #7c4008 146deg, #fff4c4 184deg, #f5b843 220deg, #9a5a0d 252deg, #fde68a 288deg, #f59e0b 324deg, #fff4c4 360deg)",
                padding: 14,
                boxShadow:
                  "0 0 55px rgba(253,224,71,0.55), 0 6px 22px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(0,0,0,0.35), inset 0 0 0 3px rgba(253,224,71,0.35)",
              }}
            >
              {/* Layer 2 — rivet ring (studded gold beads) */}
              <div
                className="w-full h-full rounded-full relative"
                style={{
                  background:
                    "repeating-conic-gradient(from 0deg, #fff7cc 0deg 4deg, #9a5a0d 4deg 8deg)",
                  padding: 4,
                  boxShadow:
                    "inset 0 0 0 1px rgba(0,0,0,0.4), inset 0 0 0 2px rgba(253,224,71,0.6)",
                }}
              >
                {/* Layer 3 — inner gold ring with brushed finish */}
                <div
                  className="w-full h-full rounded-full"
                  style={{
                    background:
                      "conic-gradient(from 90deg, #fde68a 0deg, #b45309 60deg, #fef3c7 120deg, #b45309 180deg, #fde68a 240deg, #78350f 300deg, #fde68a 360deg)",
                    padding: 6,
                    boxShadow:
                      "inset 0 0 0 2px rgba(0,0,0,0.45), inset 0 4px 10px rgba(0,0,0,0.35)",
                  }}
                >
                  {/* Layer 4 — dark inner ring where canvas sits */}
                  <div
                    className="w-full h-full rounded-full relative"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 30%, #0c3525 0%, #05201a 60%, #03150f 100%)",
                      boxShadow:
                        "inset 0 0 0 2px rgba(253,224,71,0.55), inset 0 4px 18px rgba(0,0,0,0.65)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Pointer — Baji style (small gold arrow + ball) */}
            <div
              className="absolute left-1/2 -translate-x-1/2 z-30 pointer-bob"
              style={{
                top: 2,
                width: 0,
                height: 0,
                borderLeft: "14px solid transparent",
                borderRight: "14px solid transparent",
                borderTop: "26px solid #fde047",
                filter:
                  "drop-shadow(0 2px 3px rgba(0,0,0,0.6)) drop-shadow(0 0 8px rgba(253,224,71,0.9))",
              }}
            />
            <div
              className="absolute left-1/2 -translate-x-1/2 z-30 rounded-full"
              style={{
                top: -12,
                width: 18,
                height: 18,
                background:
                  "radial-gradient(circle at 35% 28%, #fff7cc, #f59e0b 55%, #78350f 100%)",
                boxShadow:
                  "0 2px 6px rgba(0,0,0,0.55), inset 0 1px 2px rgba(255,255,255,0.7), 0 0 12px rgba(253,224,71,0.85)",
                border: "1.5px solid #78350f",
              }}
            />

            {/* Canvas wheel */}
            <canvas
              ref={canvasRef}
              width={400}
              height={400}
              className="absolute left-1/2 top-1/2 z-10 rounded-full"
              style={{
                width: "calc(100% - 56px)",
                height: "calc(100% - 56px)",
                transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                transition: spinning
                  ? (skipAnim
                      ? "transform 0.3s ease-out"
                      : "transform 5s cubic-bezier(0.17, 0.67, 0.16, 0.99)")
                  : "none",
                filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.5))",
              }}
              data-testid="free-spin-wheel"
            />

            {/* Glass reflection */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none rounded-full"
              style={{
                width: "calc(100% - 56px)",
                height: "calc(100% - 56px)",
                background:
                  "radial-gradient(ellipse 75% 45% at 50% 14%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0) 65%)",
                mixBlendMode: "screen",
              }}
            />

            {/* Center hub — Maxlive88 logo */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 rounded-full flex items-center justify-center w-[26%] h-[26%]"
              style={{
                background: "radial-gradient(circle at 35% 25%, #fff7cc 0%, #facc15 35%, #7c2d12 100%)",
                boxShadow:
                  "0 8px 22px rgba(0,0,0,0.6), inset 0 2px 6px rgba(255,255,255,0.4), inset 0 -4px 10px rgba(120,53,15,0.55), 0 0 0 4px #052116, 0 0 0 6px #f59e0b",
              }}
            >
              <div className="flex flex-col items-center leading-none">
                <span
                  className="font-black text-[12px] sm:text-[16px]"
                  style={{
                    color: "#7c2d12",
                    textShadow: "0 1px 0 rgba(255,255,255,0.35)",
                  }}
                >
                  MAX
                </span>
                <span
                  className="font-black text-[13px] sm:text-[18px]"
                  style={{
                    color: "#b91c1c",
                    textShadow: "0 1px 0 rgba(255,255,255,0.25)",
                  }}
                >
                  LIVE88
                </span>
              </div>
            </div>

            {/* JACKPOT stamp */}
            {celebrate && result?.amount > 0 && result.amount >= jackpot && (
              <div
                className="absolute left-1/2 top-1/2 z-40 stamp-pop select-none"
                style={{
                  color: "#ef4444",
                  fontWeight: 900,
                  fontSize: 54,
                  letterSpacing: "0.08em",
                  padding: "6px 18px",
                  border: "5px solid #ef4444",
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.9)",
                  boxShadow: "0 12px 30px rgba(239,68,68,0.45)",
                  fontFamily: "Impact, 'Arial Black', system-ui, sans-serif",
                }}
              >
                JACKPOT!
              </div>
            )}
          </div>

          {/* ===== Premium Info Card — combines legend, points, timer, skip-anim ===== */}
          <div
            className="mt-5 w-full max-w-md rounded-2xl p-3.5 relative overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0.35))",
              border: "1px solid rgba(253,224,71,0.28)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 26px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.4)",
            }}
            data-testid="free-spin-info-card"
          >
            {/* Decorative corner sparkles */}
            <Sparkles className="absolute -top-1 -right-1 text-amber-300/30" size={36} />

            {/* Status row — ready / countdown */}
            <div
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl mb-3"
              style={{
                background: status.can_spin
                  ? "linear-gradient(180deg, rgba(16,185,129,0.18), rgba(16,185,129,0.06))"
                  : "linear-gradient(180deg, rgba(120,53,15,0.25), rgba(120,53,15,0.08))",
                border: `1px solid ${status.can_spin ? "rgba(16,185,129,0.45)" : "rgba(253,224,71,0.25)"}`,
              }}
              data-testid="free-spin-reset-bar"
            >
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold">
                {status.can_spin ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                    </span>
                    <span className="text-emerald-200">Free spin <span className="text-white">ready</span> — go!</span>
                  </>
                ) : (
                  <>
                    <Clock size={14} className="text-amber-300" />
                    <span className="text-amber-100">Resets in</span>
                  </>
                )}
              </span>
              {!status.can_spin && (
                <span
                  className="px-2.5 py-0.5 rounded-md font-black tabular-nums text-[13px]"
                  style={{
                    background: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    border: "1px solid rgba(253,224,71,0.4)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}
                >
                  {fmtTime(countdown)}
                </span>
              )}
            </div>

            {/* 2x2 mini-info grid */}
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-black/30 border border-white/8">
                <GoldTaka size={16} />
                <div className="leading-tight">
                  <div className="text-white/55 text-[10px] uppercase tracking-wider">Bonus</div>
                  <div className="text-white font-semibold">Cash prize</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-black/30 border border-white/8">
                <GoldFS size={16} />
                <div className="leading-tight">
                  <div className="text-white/55 text-[10px] uppercase tracking-wider">Free spin</div>
                  <div className="text-white font-semibold">Bonus spins</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-black/30 border border-amber-300/20">
                <Trophy size={16} className="text-amber-300" />
                <div className="leading-tight min-w-0">
                  <div className="text-white/55 text-[10px] uppercase tracking-wider">Your points</div>
                  <div className="text-white font-bold tabular-nums truncate">{points}</div>
                </div>
              </div>
              <label className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-black/30 border border-white/8 cursor-pointer select-none">
                <div className="leading-tight min-w-0">
                  <div className="text-white/55 text-[10px] uppercase tracking-wider">Animation</div>
                  <div className="text-white font-semibold truncate">{skipAnim ? "Skip" : "Smooth"}</div>
                </div>
                <input
                  type="checkbox"
                  checked={skipAnim}
                  onChange={(e) => setSkipAnim(e.target.checked)}
                  className="w-4 h-4 accent-emerald-500 shrink-0"
                  data-testid="free-spin-skip-anim"
                />
              </label>
            </div>

            <div className="text-[10.5px] text-white/45 mt-2.5 px-1 text-center">
              Only normal deposits count toward points. Bonuses may carry a wagering requirement.
            </div>
          </div>

          {/* ===== Result card ===== */}
          {result && (
            <div
              className="mt-5 rounded-2xl p-5 w-full max-w-md text-center fade-in-up"
              style={{
                background:
                  result.amount > 0
                    ? "linear-gradient(135deg, rgba(16,185,129,0.3), rgba(16,185,129,0.08))"
                    : "linear-gradient(135deg, rgba(107,114,128,0.18), rgba(107,114,128,0.04))",
                border: `1px solid ${result.amount > 0 ? "rgba(16,185,129,0.55)" : "rgba(107,114,128,0.35)"}`,
                boxShadow: result.amount > 0 ? "0 14px 40px rgba(16,185,129,0.3)" : "none",
              }}
              data-testid="free-spin-result"
            >
              {result.amount > 0 ? (
                <>
                  <div className="text-emerald-300 text-[11px] font-bold tracking-[0.24em] uppercase inline-flex items-center gap-1.5">
                    <Trophy size={12} /> You Won!
                  </div>
                  <div
                    className="text-4xl font-black mt-2 bg-clip-text text-transparent"
                    style={{ backgroundImage: "linear-gradient(90deg, #6ee7b7, #34d399, #10b981)" }}
                  >
                    {result.label}
                  </div>
                  <div className="text-sm text-white/85 mt-1">
                    BDT <span className="font-bold text-emerald-200">{result.amount.toFixed(2)}</span> added to your balance
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs text-gray-300 font-semibold uppercase tracking-wider">{result.label}</div>
                  <div className="text-sm text-white/70 mt-1">
                    Better luck next time. Come back later for another spin!
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== Premium Spin button ===== */}
          <div className="mt-6 w-full max-w-xl relative">
            {/* FREE pill badge — shows when ready */}
            {status.can_spin && !spinning && (
              <span
                className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase"
                style={{
                  background: "linear-gradient(180deg, #ef4444, #991b1b)",
                  color: "#fff7d6",
                  border: "1px solid rgba(254,243,199,0.6)",
                  boxShadow:
                    "0 4px 12px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.35)",
                  animation: "spinIconHalo 1.6s ease-in-out infinite",
                }}
              >
                <Sparkles size={9} className="inline -mt-0.5 mr-1" />
                100% Free
              </span>
            )}

            <button
              onClick={spin}
              disabled={spinning || !status.can_spin}
              className="w-full h-16 rounded-2xl text-lg sm:text-xl font-black text-white uppercase tracking-[0.14em] relative overflow-hidden group transition-transform active:scale-[0.98] disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{
                background: status.can_spin
                  ? "linear-gradient(180deg, #34d399 0%, #10b981 38%, #047857 100%)"
                  : "linear-gradient(180deg, #4b5563 0%, #374151 100%)",
                border: status.can_spin
                  ? "1.5px solid rgba(253,224,71,0.85)"
                  : "1px solid rgba(255,255,255,0.08)",
                boxShadow: status.can_spin
                  ? "0 16px 38px rgba(16,185,129,0.55), inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -4px 0 rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.4), 0 0 28px rgba(253,224,71,0.25)"
                  : "0 8px 20px rgba(0,0,0,0.35)",
                textShadow: "0 2px 0 rgba(0,0,0,0.4)",
                opacity: spinning ? 0.85 : 1,
              }}
              data-testid="free-spin-btn"
            >
              {/* Decorative side sparkles */}
              {status.can_spin && !spinning && (
                <>
                  <Sparkles size={16} className="text-amber-200 relative z-10 drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]" />
                </>
              )}
              <span className="relative z-10">
                {spinning ? "Spinning..." : status.can_spin ? "Spin to win!" : "Not ready yet"}
              </span>
              {status.can_spin && !spinning && (
                <Sparkles size={16} className="text-amber-200 relative z-10 drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]" />
              )}
              {/* Shimmer sweep */}
              {status.can_spin && !spinning && (
                <span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
                    transform: "translateX(-100%)",
                    animation: "shimmerButton 2.4s linear infinite",
                  }}
                />
              )}
            </button>
          </div>

          {/* ===== How It Works strip ===== */}
          <div className="mt-7 w-full max-w-2xl">
            <div className="text-center text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold mb-3 inline-flex items-center gap-2 justify-center w-full">
              <span className="h-px w-10 bg-amber-300/30" />
              How It Works
              <span className="h-px w-10 bg-amber-300/30" />
            </div>
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
              {[
                { n: "1", title: "Tap Spin", desc: "Hit the button — wheel spins instantly" },
                { n: "2", title: "Win Big",  desc: "Land on a prize sector to win" },
                { n: "3", title: "Cash In",  desc: "Reward credits to balance instantly" },
              ].map((s) => (
                <div
                  key={s.n}
                  className="rounded-xl p-2.5 sm:p-3 text-center relative"
                  style={{
                    background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.25))",
                    border: "1px solid rgba(253,224,71,0.18)",
                  }}
                >
                  <div
                    className="absolute -top-2.5 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full flex items-center justify-center text-[12px] font-black"
                    style={{
                      background: "linear-gradient(180deg, #fde047, #b45309)",
                      color: "#5b2e05",
                      border: "1.5px solid #b45309",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.5)",
                    }}
                  >
                    {s.n}
                  </div>
                  <div className="text-[12px] font-black text-amber-200 mt-2">{s.title}</div>
                  <div className="text-[10px] text-white/60 mt-0.5 leading-tight">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rules modal */}
        {showRules && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.7)" }}
            onClick={() => setShowRules(false)}
            data-testid="free-spin-rules-modal"
          >
            <div
              className="max-w-md w-full rounded-2xl p-6 relative"
              style={{
                background: "linear-gradient(180deg, #0b2a1e, #051a12)",
                border: "1px solid rgba(253,224,71,0.3)",
                boxShadow: "0 22px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(253,224,71,0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowRules(false)}
                className="absolute right-3 top-3 p-1.5 rounded-full hover:bg-white/10"
                data-testid="free-spin-rules-close"
              >
                <X size={18} className="text-white/80" />
              </button>
              <div className="text-amber-200 font-black text-xl mb-3 tracking-wide">RULES</div>
              <ul className="text-white/80 text-sm space-y-2 list-disc pl-5">
                <li>Every eligible user gets <b>1 free spin</b> every {Math.round(status.cooldown_hours)} hours.</li>
                <li>Prizes are credited <b>instantly</b> to your main balance.</li>
                <li>Bonuses may carry a wagering requirement as per admin settings.</li>
                <li>Only normal deposits count toward your <b>points</b>.</li>
                <li>Abuse / multi-account will forfeit winnings.</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmerButton {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes spinShimmerText {
          0%   { transform: translateX(-100%); }
          60%  { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes bulbTwinkle {
          0%, 100% { opacity: 1;   transform: translate(-50%, -50%) scale(1); }
          50%      { opacity: 0.35; transform: translate(-50%, -50%) scale(0.7); }
        }
        @keyframes spinIconHalo {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%      { opacity: 1;     transform: scale(1.06); }
        }
      `}</style>
    </ProfileShell>
  );
}

/* ---------- Sub-components ---------- */

function TierCard({ tier, active, onClick }) {
  const disabled = !tier.free;
  return (
    <button
      onClick={onClick}
      className="relative shrink-0 rounded-xl px-3 pt-2 pb-1.5 transition-transform active:scale-95"
      style={{
        width: 96,
        height: 90,
        background: active
          ? "linear-gradient(180deg, rgba(253,224,71,0.18), rgba(253,224,71,0.04))"
          : "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.2))",
        border: active
          ? "1px solid rgba(253,224,71,0.7)"
          : "1px solid rgba(255,255,255,0.08)",
        boxShadow: active ? "0 0 24px rgba(253,224,71,0.35)" : "none",
        opacity: disabled ? 0.55 : 1,
      }}
      data-testid={`free-spin-tier-${tier.id}`}
    >
      <TreasureIcon free={tier.free} big={active} />
      <div
        className="mt-1 rounded-full px-2 py-0.5 mx-auto w-[80%] text-[11px] font-black inline-flex items-center justify-center gap-1"
        style={{
          background: "linear-gradient(180deg, #fde68a, #d97706)",
          color: "#5b2e05",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.35)",
          border: "1px solid #b45309",
        }}
      >
        <GoldTaka size={10} />
        <span>{tier.label}</span>
      </div>
    </button>
  );
}

function TreasureIcon({ free }) {
  // Use a CSS-drawn treasure (gold pile) stack
  if (free) {
    return (
      <div className="mx-auto flex items-center justify-center" style={{ width: 48, height: 44 }}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: "radial-gradient(circle at 35% 25%, #fffbe6 0%, #34d399 40%, #065f46 100%)",
            boxShadow: "0 6px 14px rgba(0,0,0,0.45), inset 0 1px 2px rgba(255,255,255,0.6)",
            border: "2px solid #fbbf24",
          }}
        >
          <span className="text-white font-black text-[11px]">FREE</span>
        </div>
      </div>
    );
  }
  // Gold pile / chest representation
  return (
    <div className="mx-auto relative" style={{ width: 52, height: 44 }}>
      <div
        className="absolute inset-x-0 bottom-0 mx-auto rounded-md"
        style={{
          width: 46,
          height: 22,
          background:
            "linear-gradient(180deg, #86efac 0%, #10b981 50%, #065f46 100%)",
          border: "1.5px solid #fbbf24",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
        }}
      />
      {/* coins stack on top */}
      <div className="absolute inset-x-0 top-0 flex items-end justify-center gap-0.5">
        {[14, 18, 14].map((h, i) => (
          <span
            key={i}
            className="rounded-full"
            style={{
              width: 10,
              height: h,
              background: "linear-gradient(180deg, #fef3c7, #f59e0b 60%, #78350f)",
              border: "1px solid #92400e",
              boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function GoldCoin({ size = 14 }) {
  return (
    <span
      className="inline-block rounded-full"
      style={{
        width: size,
        height: size,
        background: "radial-gradient(circle at 35% 28%, #fffbe6 0%, #fde047 45%, #b45309 100%)",
        border: "1px solid #7c2d12",
        boxShadow: "0 1px 2px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.5)",
      }}
    />
  );
}

function GoldTaka({ size = 14 }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-black"
      style={{
        width: size,
        height: size,
        background: "radial-gradient(circle at 35% 28%, #fffbe6 0%, #fde047 45%, #b45309 100%)",
        border: "1px solid #7c2d12",
        color: "#7c2d12",
        fontSize: size * 0.7,
        lineHeight: 1,
      }}
    >
      ৳
    </span>
  );
}

function GoldFS({ size = 14 }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-black"
      style={{
        width: size,
        height: size,
        background: "radial-gradient(circle at 35% 28%, #fffbe6 0%, #fde047 45%, #b45309 100%)",
        border: "1px solid #7c2d12",
        color: "#7c2d12",
        fontSize: size * 0.52,
        letterSpacing: -0.5,
        lineHeight: 1,
      }}
    >
      FS
    </span>
  );
}

function Confetti() {
  const colors = ["#f59e0b", "#10b981", "#3b82f6", "#fde68a", "#ef4444", "#22c55e"];
  const pieces = Array.from({ length: 70 }).map((_, i) => ({
    left: Math.random() * 100,
    cx: (Math.random() - 0.5) * 220,
    delay: Math.random() * 0.7,
    color: colors[i % colors.length],
    rot: Math.random() * 360,
    i,
  }));
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-40" style={{ height: 760 }}>
      {pieces.map((p) => (
        <span
          key={p.i}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            transform: `rotate(${p.rot}deg)`,
            "--cx": `${p.cx}px`,
          }}
        />
      ))}
    </div>
  );
}

function CoinRain() {
  const pieces = Array.from({ length: 26 }).map((_, i) => ({
    left: 8 + Math.random() * 84,
    cx: (Math.random() - 0.5) * 180,
    delay: Math.random() * 1.0,
    i,
  }));
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-40" style={{ height: 800 }}>
      {pieces.map((p) => (
        <span
          key={p.i}
          className="coin-piece"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            "--cx": `${p.cx}px`,
          }}
        />
      ))}
    </div>
  );
}

/* ---------- Baji-style wheel canvas ---------- */
function drawBajiWheel(canvas, prizes) {
  const ctx = canvas.getContext("2d");
  const { width: w, height: h } = canvas;
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(cx, cy) - 4;
  const n = prizes.length;
  const segAngle = (Math.PI * 2) / n;

  ctx.clearRect(0, 0, w, h);

  // Alternating deep-green / deep-blue palette like Baji
  const greenBase = ["#0f7a4c", "#0b5c38"];
  const blueBase  = ["#1e5f8a", "#143f5b"];

  for (let i = 0; i < n; i++) {
    const start = -Math.PI / 2 + i * segAngle;
    const end = start + segAngle;
    const isGreen = i % 2 === 0;
    const palette = isGreen ? greenBase : blueBase;

    const grad = ctx.createRadialGradient(cx, cy, radius * 0.15, cx, cy, radius);
    grad.addColorStop(0, palette[1]);
    grad.addColorStop(0.55, palette[0]);
    grad.addColorStop(1, isGreen ? "#14965f" : "#2577a8");

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, start, end);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Strong diagonal hatching pattern (clipped to segment)
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, start, end);
    ctx.closePath();
    ctx.clip();
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = isGreen ? "#1dbf7a" : "#44a0d8";
    ctx.lineWidth = 1.2;
    // diagonal lines at 45 deg across entire area
    for (let k = -radius * 1.5; k < radius * 1.5; k += 5) {
      ctx.beginPath();
      ctx.moveTo(cx + k - radius, cy - radius);
      ctx.lineTo(cx + k + radius, cy + radius);
      ctx.stroke();
    }
    ctx.restore();

    // bright golden segment divider line
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(start) * radius, cy + Math.sin(start) * radius);
    ctx.strokeStyle = "#fde68a";
    ctx.lineWidth = 2.5;
    ctx.shadowColor = "rgba(253,224,71,0.8)";
    ctx.shadowBlur = 3;
    ctx.stroke();
    ctx.restore();

    // Prize label — radial text with small "x" and big number
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(start + segAngle / 2);
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.75)";
    ctx.shadowBlur = 4;
    const rawLabel = String(prizes[i].label || "");
    const amount = Number(prizes[i].amount) || 0;
    let display = rawLabel;
    let isNumLabel = false;
    if (amount > 0) {
      const clean = rawLabel.replace(/[^0-9]/g, "") || String(amount);
      display = clean;
      isNumLabel = true;
    } else {
      display = rawLabel || "Try Again";
    }
    if (isNumLabel) {
      // small "x" then big number (Baji style: "x100")
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 16px 'Arial Black', system-ui, sans-serif";
      const xWidth = ctx.measureText("x").width;
      ctx.font = "900 26px 'Arial Black', system-ui, sans-serif";
      const numWidth = ctx.measureText(display).width;
      const totalW = xWidth + numWidth + 2;
      const rightEdge = radius - 12;
      // number (big) — rightmost
      ctx.fillText(display, rightEdge, 2);
      // x (small) — left of number
      ctx.font = "900 16px 'Arial Black', system-ui, sans-serif";
      ctx.fillText("x", rightEdge - numWidth - 2, -2);
      void totalW;
    } else {
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 18px 'Arial Black', system-ui, sans-serif";
      ctx.fillText(display, radius - 14, 2);
    }
    ctx.restore();

    // FS gold coin badge in middle of segment
    const midR = radius * 0.54;
    const midA = start + segAngle / 2;
    const bx = cx + Math.cos(midA) * midR;
    const by = cy + Math.sin(midA) * midR;
    // outer gold ring
    const coinGrad = ctx.createRadialGradient(bx - 3, by - 3, 2, bx, by, 13);
    coinGrad.addColorStop(0, "#fffbe6");
    coinGrad.addColorStop(0.55, "#fde047");
    coinGrad.addColorStop(1, "#8a4508");
    ctx.beginPath();
    ctx.arc(bx, by, 12, 0, Math.PI * 2);
    ctx.fillStyle = coinGrad;
    ctx.fill();
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = "#5b2e05";
    ctx.stroke();
    // inner green FS circle
    const innerGrad = ctx.createRadialGradient(bx - 2, by - 2, 1, bx, by, 9);
    innerGrad.addColorStop(0, "#34d399");
    innerGrad.addColorStop(1, "#065f46");
    ctx.beginPath();
    ctx.arc(bx, by, 7.5, 0, Math.PI * 2);
    ctx.fillStyle = innerGrad;
    ctx.fill();
    ctx.fillStyle = "#fde68a";
    ctx.font = "900 8px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowBlur = 0;
    ctx.fillText("FS", bx, by + 0.5);
  }

  // Decorative inner rim line (gold)
  ctx.beginPath();
  ctx.arc(cx, cy, radius - 1, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(253,224,71,0.75)";
  ctx.lineWidth = 2.5;
  ctx.shadowColor = "rgba(253,224,71,0.6)";
  ctx.shadowBlur = 4;
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Tiny tick-mark dashes around rim (like Baji)
  const ticks = 80;
  for (let t = 0; t < ticks; t++) {
    const ang = (Math.PI * 2 / ticks) * t;
    const x1 = cx + Math.cos(ang) * (radius - 6);
    const y1 = cy + Math.sin(ang) * (radius - 6);
    const x2 = cx + Math.cos(ang) * (radius - 2);
    const y2 = cy + Math.sin(ang) * (radius - 2);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = t % 2 === 0 ? "#fde68a" : "rgba(120,53,15,0.7)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}
